<script setup lang="ts">
import type { FormError } from "#ui/types";
import { z } from "zod/v4";
import type { Database } from "~/database.types";

definePageMeta({
  layout: "no-link-header",
});

const supabase = useSupabaseClient<Database>();
const user = useSupabaseUser();

const loading = ref(false);
const error = ref("");
const success = ref(false);

const schema = z.object({
  email: z.email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
});

type LoginForm = z.infer<typeof schema>;

const validate = (state: LoginForm) => {
  const errors: FormError[] = [];
  if (!state.email)
    errors.push({ path: "email", message: "E-Mail darf nicht leer sein" });
  return errors;
};

const handleLogin = async (data: LoginForm) => {
  if (success.value) {
    return;
  }
  loading.value = true;
  error.value = "";
  success.value = false;
  const response = await supabase.auth.signInWithOtp({
    email: data.email,
  });
  if (response.error) {
    error.value = response.error.message;
    success.value = false;
  } else {
    error.value = "";
    success.value = true;
  }
  loading.value = false;
};

watch(
  user,
  () => {
    if (user.value) {
      return navigateTo("/");
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="flex flex-col items-center pt-20">
    <UCard class="w-full max-w-sm shadow-lg">
      <UAuthForm
        title="Melde dich jetzt an."
        description="Wir schicken dir per E-Mail einen Link, damit du auf Jobmate zugreifen kannst."
        :schema="schema"
        :fields="[
          {
            name: 'email',
            type: 'email',
            label: 'Email',
            placeholder: 'E-Mail eingeben',
          },
        ]"
        :submit-button="{
          label: 'Zugriff anfordern',
          color: 'primary',
          loading,
          size: 'xl',
        }"
        :validate="validate"
        :ui="{
          title: 'text-3xl',
          base: 'text-center',
          description: 'text-gray-950 mt-4 text-lg',
        }"
        @submit="handleLogin"
      />
      <template #footer>
        <div class="text-center text-sm text-gray-500">
          Informationen über den Datenschutz findest Du in unserer
          <a
            href="https://drive.google.com/file/d/1JGmAguaad0MekAolRHn_XbfDT0Sfimyj/view"
            class="underline"
          >
            Datenschutzerklärung.
          </a>
          Für die Auftragsverarbeitung im Zusammenhang mit Jobmate gilt unser
          <a
            href="https://drive.google.com/file/d/1hplysay4u44pJl9fKQ7Eue0g0QFVkXFF/view"
            class="underline"
          >
            Auftragsverarbeitungsvertrag.
          </a>
        </div>
      </template>
    </UCard>

    <div class="w-full max-w-sm">
      <UAlert
        v-if="error"
        class="mt-8 bg-red-50 text-red-500"
        variant="soft"
        title="Etwas hat nicht geklappt."
        description="Für diese E-Mail-Adresse gibt es kein Konto. Du kannst ausschliessliche eine E-Mail-Adresse verwenden, welche zuvor bei uns hinterlegt wurde."
        :ui="{
          title: 'text-lg font-semibold mb-1 leading-relaxed',
          description: 'text-lg',
        }"
      />

      <UAlert
        v-if="success"
        class="mt-8 bg-green-50 text-green-500"
        variant="soft"
        title="Schau in deinen Posteingang."
        description="Wir haben dir per E-Mail eine Link geschickt."
        :ui="{
          title: 'text-lg font-semibold mb-1 leading-relaxed',
          description: 'text-lg',
        }"
      />
    </div>

    <div class="py-20 text-center">
      <h4 class="pb-4 text-gray-900">
        Hast du kein Konto und möchtest Jobmate auch nutzen?
      </h4>
      <a href="mailto:hello@jobmate.ch" class="text-lg text-violet-400 underline">
        Nimm mit uns Kontakt auf
      </a>
    </div>
  </div>
</template>
